"use client";

import { useState, useEffect } from "react";
import Icon from "@/components/ui/AppIcon";
import type { Transaction } from "./TransactionCard";
import { createClient } from "@/lib/supabase/client";

interface TransactionModalProps {
  transaction: Transaction | null;
  onClose: () => void;
  isAdmin?: boolean;
  onRefresh?: () => void;
}

const AREAS = [
  "Port of Spain",
  "San Fernando",
  "Chaguanas",
  "Westmoorings",
  "Maraval",
  "Diego Martin",
  "Arima",
  "Couva",
  "Point Fortin",
  "Tobago",
  "Penal",
  "Siparia",
  "Sangre Grande",
  "Princes Town",
  "Other",
];

const PROPERTY_TYPES = [
  "House",
  "Apartment",
  "Townhouse",
  "Land",
  "Commercial",
  "Other",
];

const RENTAL_FREQUENCIES = ["Monthly", "Nightly"];
const RENTAL_TERMS = ["Long-term", "Short-term", "Carnival", "Corporate"];

function formatTTD(amount: number): string {
  return new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-TT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function DetailRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-border last:border-0">
      <span className="text-xs text-muted font-body uppercase tracking-wide">{label}</span>
      <span
        className={`text-sm font-medium text-right max-w-[60%] ${highlight ? "font-heading font-bold text-navy text-base" : "text-navy font-body"
          }`}
      >
        {value}
      </span>
    </div>
  );
}

export default function TransactionModal({
  transaction: t,
  onClose,
  isAdmin,
  onRefresh,
}: TransactionModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit form state
  const [form, setForm] = useState<any>(null);

  useEffect(() => {
    if (t) {
      setForm({
        ...t,
        sale_price: t.sale_price?.toString() || "",
        rental_amount: t.rental_amount?.toString() || "",
        date_sold: t.date_sold || "",
      });
      setIsEditing(false);
      setError(null);
    }
  }, [t]);

  useEffect(() => {
    if (!t) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSaving && !isDeleting) onClose();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [t, onClose, isSaving, isDeleting]);

  if (!t) return null;

  const isSale = t.transaction_type === "Sale";

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this transaction permanently?")) return;

    try {
      setIsDeleting(true);
      const supabase = createClient();
      const { error } = await supabase.from("transactions").delete().eq("id", t.id);

      if (error) throw error;

      onRefresh?.();
      onClose();
    } catch (err: any) {
      console.error("Delete failed:", err);
      setError("Failed to delete transaction.");
      setIsDeleting(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      setError(null);
      const supabase = createClient();

      const updateData: any = {
        street_address: form.street_address,
        area: form.area,
        property_type: form.property_type,
        notes: form.notes,
        transaction_type: form.transaction_type,
      };

      if (form.transaction_type === "Sale") {
        updateData.sale_price = parseFloat(form.sale_price) || null;
        updateData.date_sold = form.date_sold || null;
        updateData.rental_amount = null;
        updateData.rental_frequency = null;
        updateData.rental_term = null;
      } else {
        updateData.rental_amount = parseFloat(form.rental_amount) || null;
        updateData.rental_frequency = form.rental_frequency;
        updateData.rental_term = form.rental_term;
        updateData.sale_price = null;
        updateData.date_sold = null;
      }

      const { error } = await supabase
        .from("transactions")
        .update(updateData)
        .eq("id", t.id);

      if (error) throw error;

      setIsEditing(false);
      onRefresh?.();
      onClose(); // Close modal on success for simplicity
    } catch (err: any) {
      console.error("Update failed:", err);
      setError("Failed to update transaction.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="modal-overlay fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ backgroundColor: "rgba(17, 29, 51, 0.6)", backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && !isSaving && !isDeleting && onClose()}
    >
      <div className="modal-content bg-white rounded-xl shadow-modal w-full max-w-lg max-h-[95vh] flex flex-col my-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-border flex-shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`text-2xs font-heading font-bold px-2.5 py-1 rounded ${isSale ? "badge-sale" : "badge-rental"
                  }`}
              >
                {t.transaction_type.toUpperCase()}
              </span>
              <span className="text-2xs text-muted font-body">#{t.id}</span>
            </div>
            <h2 className="font-heading font-bold text-navy text-lg leading-snug">
              {isEditing ? "Edit Transaction" : t.street_address}
            </h2>
            {!isEditing && (
              <div className="flex items-center gap-1.5 mt-1">
                <Icon name="MapPinIcon" size={13} className="text-teal" />
                <span className="text-sm text-muted">{t.area}, Trinidad & Tobago</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={isSaving || isDeleting}
            className="p-2 rounded-md hover:bg-sand text-muted hover:text-navy transition-colors flex-shrink-0 ml-4 disabled:opacity-30"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-0">
          {isEditing ? (
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-600 text-xs rounded border border-red-200">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-2xs font-bold uppercase text-muted mb-1">Street Address</label>
                <input
                  type="text"
                  required
                  value={form.street_address}
                  onChange={(e) => setForm({ ...form, street_address: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded text-sm focus:border-teal outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-2xs font-bold uppercase text-muted mb-1">Area</label>
                  <select
                    value={form.area}
                    onChange={(e) => setForm({ ...form, area: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded text-sm"
                  >
                    {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-2xs font-bold uppercase text-muted mb-1">Prop Type</label>
                  <select
                    value={form.property_type}
                    onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded text-sm"
                  >
                    {PROPERTY_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              {isSale ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-2xs font-bold uppercase text-muted mb-1">Price (TTD)</label>
                    <input
                      type="number"
                      required
                      value={form.sale_price}
                      onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-2xs font-bold uppercase text-muted mb-1">Date Sold</label>
                    <input
                      type="date"
                      required
                      value={form.date_sold}
                      onChange={(e) => setForm({ ...form, date_sold: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded text-sm"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-2xs font-bold uppercase text-muted mb-1">Amount (TTD)</label>
                      <input
                        type="number"
                        required
                        value={form.rental_amount}
                        onChange={(e) => setForm({ ...form, rental_amount: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-2xs font-bold uppercase text-muted mb-1">Frequency</label>
                      <select
                        value={form.rental_frequency}
                        onChange={(e) => setForm({ ...form, rental_frequency: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded text-sm"
                      >
                        {RENTAL_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-2xs font-bold uppercase text-muted mb-1">Term</label>
                    <div className="flex flex-wrap gap-2">
                      {RENTAL_TERMS.map(term => (
                        <button
                          key={term}
                          type="button"
                          onClick={() => setForm({ ...form, rental_term: term })}
                          className={`px-3 py-1.5 rounded border text-xs font-semibold ${form.rental_term === term ? 'bg-teal/10 border-teal text-teal' : 'bg-sand border-border text-muted'}`}
                        >
                          {term}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-2xs font-bold uppercase text-muted mb-1">Notes</label>
                <textarea
                  value={form.notes || ""}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded text-sm focus:border-teal outline-none resize-none"
                />
              </div>

              <div className="pt-4 border-t border-border flex gap-3">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 bg-teal hover:bg-teal-light text-white font-bold py-2.5 rounded-lg text-sm transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  {isSaving ? <Icon name="ArrowPathIcon" size={16} className="animate-spin" /> : <Icon name="CheckIcon" size={16} />}
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  disabled={isSaving}
                  className="px-6 py-2.5 border border-border text-muted font-bold rounded-lg text-sm hover:bg-sand transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              {/* Price highlight */}
              <div className="px-6 py-5 bg-sand/60 border-b border-border">
                <p className="text-xs text-muted mb-1 uppercase tracking-wide font-body">
                  {isSale ? "Sale Price" : `Rental · ${t.rental_frequency}`}
                </p>
                <p className="font-heading font-extrabold text-navy text-3xl">
                  {isSale
                    ? t.sale_price
                      ? formatTTD(t.sale_price)
                      : "Not disclosed"
                    : t.rental_amount
                      ? formatTTD(t.rental_amount)
                      : "Not disclosed"}
                </p>
              </div>

              {/* Details */}
              <div className="px-6 py-2">
                <DetailRow label="Property Type" value={t.property_type} />
                <DetailRow label="Area" value={t.area} />
                {isSale && t.date_sold && (
                  <DetailRow label="Date Sold" value={formatDate(t.date_sold)} />
                )}
                {!isSale && t.rental_term && (
                  <DetailRow label="Rental Term" value={t.rental_term} />
                )}
                {!isSale && t.rental_frequency && (
                  <DetailRow label="Rental Frequency" value={t.rental_frequency} />
                )}
                {t.notes && <DetailRow label="Notes" value={t.notes} />}
              </div>

              {/* Meta */}
              <div className="mx-6 my-4 p-4 bg-navy-50 rounded-lg border border-navy-100">
                <p className="text-2xs text-muted uppercase tracking-widest mb-2 font-heading font-semibold">
                  Submission Details
                </p>
                <div className="flex items-center justify-between text-xs text-navy">
                  <div className="flex items-center gap-1.5">
                    <Icon name="UserIcon" size={13} className="text-muted" />
                    <span>{t.submitted_by}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Icon name="CalendarDaysIcon" size={13} className="text-muted" />
                    <span>{formatDate(t.submitted_date)}</span>
                  </div>
                </div>
              </div>

              {/* Admin Actions */}
              {isAdmin && (
                <div className="px-6 py-5 border-t border-border bg-sand/30 flex gap-3">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex-1 flex items-center justify-center gap-2 bg-white border border-border text-navy font-bold py-2.5 rounded-lg text-sm hover:border-teal hover:text-teal transition-all shadow-sm"
                  >
                    <Icon name="PencilSquareIcon" size={16} />
                    Edit Record
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="flex-1 flex items-center justify-center gap-2 bg-white border border-border text-red-600 font-bold py-2.5 rounded-lg text-sm hover:border-red-600 hover:bg-red-50 transition-all shadow-sm disabled:opacity-50"
                  >
                    {isDeleting ? <Icon name="ArrowPathIcon" size={16} className="animate-spin" /> : <Icon name="TrashIcon" size={16} />}
                    Delete
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Disclaimer - only show when not editing */}
        {!isEditing && (
          <div className="mx-6 mb-6 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex-shrink-0">
            <p className="text-2xs text-amber-800 leading-relaxed font-body">
              <strong>Data Disclaimer:</strong> This transaction was submitted voluntarily and has not been independently verified.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
